import {
  ExtendLogo,
  SelectionCardV3,
  TypographyV3,
} from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { useGate } from 'statsig-react';
import type { MessageKey } from 'types/LocaleMessageKey';

import { useWizardStateContext } from '@/app/[locale]/product/_contexts/WizardStateProvider';
import WarrantySelection from '@/app/[locale]/product/_steps/enums/WarrantySelection';
import useCurrency from '@/app/hooks/useCurrency';

import upgradeWarrantyBenefits from './upgradeWarrantyBenefits';

const tValue = (hasMaxRingCount: boolean, t: (val: MessageKey) => string) => ({
  logo: function logo() {
    return (
      <ExtendLogo
        aria-label={t('extend')}
        className={cx('mb-1 ml-1 inline h-[16px] w-[75.2px]', {
          'opacity-20': hasMaxRingCount,
        })}
      />
    );
  },
});

const BundleUsSection = (): JSX.Element => {
  const {
    scrollRef,
    hasMaxRingCount,
    threeYearWarranty,
    twoYearWarranty,
    warrantySelection,
    setWarrantySelection,
  } = useWizardStateContext();
  const hsaFsaGate = useGate('hsafsa_rollout').value;
  const { currencyCode } = useCurrency();
  const [twoYearOpen, setTwoYearOpen] = useState(false);
  const [threeYearOpen, setThreeYearOpen] = useState(false);
  const t = useTranslations();

  const warranyBenefits = upgradeWarrantyBenefits(t);
  const typographyColor = hasMaxRingCount ? 'disabled' : 'dark';

  const handleAccordionClick = (section: 'two-year' | 'three-year') => {
    if (hasMaxRingCount) return;
    if (section === 'two-year') {
      setTwoYearOpen(!twoYearOpen);
    } else {
      setThreeYearOpen(!threeYearOpen);
    }
  };

  const handleWarrantyCheckBoxClick = (warrantyInput: WarrantySelection) => {
    if (warrantySelection === warrantyInput) {
      setWarrantySelection(WarrantySelection.OneYear);
    } else if (warrantySelection !== warrantyInput) {
      setWarrantySelection(warrantyInput);
    }
  };

  return (
    <>
      <div
        className={cx(
          'pt-5 opacity-50 md:w-[1px] md:bg-sandstone-500 md:pt-0',
          {
            'md:bg-gray-300 md:opacity-10': hasMaxRingCount,
          },
        )}
      />
      <div className="md:w-[46%]" data-cy="bundle-us-section">
        <TypographyV3
          className="pb-3 text-xl font-medium md:pb-4"
          color={typographyColor}
        >
          <span />
          {t.rich('pdp_warranty_step_title_new', tValue(hasMaxRingCount, t))}
        </TypographyV3>
        <TypographyV3
          className="pb-5 text-base font-medium"
          color={typographyColor}
        >
          {hsaFsaGate
            ? t('bundle_modal_warranty_subtext_hsa_fsa')
            : t('bundle_modal_warranty_subtitle')}
        </TypographyV3>
        <div ref={twoYearOpen ? scrollRef : null}>
          <SelectionCardV3
            data-cy="bundle-warranty-two-year"
            AccordionContent={warranyBenefits}
            accordionOpen={twoYearOpen}
            cardSelected={warrantySelection == WarrantySelection.TwoYear}
            currencyCode={currencyCode}
            disabled={Boolean(hasMaxRingCount)}
            price={twoYearWarranty?.price?.amount ?? 45}
            title={t('2_year_protection_plan')}
            accordionOnClick={() => handleAccordionClick('two-year')}
            onCheckBoxClick={() =>
              handleWarrantyCheckBoxClick(WarrantySelection.TwoYear)
            }
          />
        </div>
        <div ref={threeYearOpen ? scrollRef : null}>
          <SelectionCardV3
            data-cy="bundle-warranty-three-year"
            AccordionContent={warranyBenefits}
            accordionOpen={threeYearOpen}
            cardSelected={warrantySelection == WarrantySelection.ThreeYear}
            currencyCode={currencyCode}
            disabled={Boolean(hasMaxRingCount)}
            price={threeYearWarranty?.price?.amount ?? 60}
            title={t('3_year_protection_plan')}
            accordionOnClick={() => handleAccordionClick('three-year')}
            onCheckBoxClick={() =>
              handleWarrantyCheckBoxClick(WarrantySelection.ThreeYear)
            }
          />
        </div>
        {hsaFsaGate && (
          <div>
            <TypographyV3
              className="pt-5 text-base font-medium"
              color={typographyColor}
            >
              <em>{t.rich('bundle_modal_warranty_hsa_fsa')}</em>
            </TypographyV3>
          </div>
        )}
      </div>
    </>
  );
};
export default BundleUsSection;
